import {IConditionFieldPreset} from "../../../policy-types";
import {
    ICheckboxesTreeItem
} from "../../../../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree/stores/checkboxes-tree-store";
import LocationTreeConverter from "./location-tree-converter";
import LocationSemanticGroupConverter from "./location-semantic-group-converter";
import LocationLabelsGrouper, {ILocationLabelsGroups} from "./location-labels-grouper";
import type {
    ILocationCompareValueList,
    ILocationPersistenceToken,
    ILocationToken,
    ILocationUiToken,
    IParsedLocationCompareValue,
} from "./location-selection-types";

/**
 * Facade for all location selection transformations.
 * Converts between persistence format, UI tokens, checkbox tree, and display labels.
 */
class LocationSelectionConverter {

    private readonly _semanticGroupConverter: LocationSemanticGroupConverter;
    private readonly _treeConverter: LocationTreeConverter;
    private readonly _labelsGrouper: LocationLabelsGrouper;

    constructor(fieldPreset: IConditionFieldPreset) {
        this._semanticGroupConverter = new LocationSemanticGroupConverter(fieldPreset);
        this._treeConverter = new LocationTreeConverter(fieldPreset, this._semanticGroupConverter);
        this._labelsGrouper = new LocationLabelsGrouper(this._semanticGroupConverter.getSemanticGroups());
    }

    /** Normalizes persistence or UI tokens into UI token shape with explicit group context. */
    public toUiTokens(tokens: ILocationToken[]): ILocationUiToken[] {
        return this._semanticGroupConverter.hydrateTokensForUi(tokens) as ILocationUiToken[];
    }

    /** Strips group context and expands full group selections for DB storage. */
    public toPersistence(tokens: ILocationToken[]): ILocationPersistenceToken[] {
        return this._semanticGroupConverter.expandTokensForPersistence(tokens);
    }

    /** Extracts UI tokens from a checkbox tree selection. */
    public fromTree(items: ICheckboxesTreeItem[] | undefined): ILocationUiToken[] | undefined {
        return this._treeConverter.extractTokensFromTree(items) as ILocationUiToken[] | undefined;
    }

    /**
     * Builds a checkbox value tree from persistence or UI tokens.
     * Returns undefined for no selection, [] for select all, or a tree for specific selection.
     */
    public toTree(tokens: ILocationToken[] | undefined): ICheckboxesTreeItem[] | undefined {
        if (!tokens) {
            return undefined;
        }

        if (tokens.length === 0) {
            return [];
        }

        return this._treeConverter.getValueTree(this.toUiTokens(tokens));
    }

    /** Builds grouped human-readable labels for tag rendering from persistence or UI tokens. */
    public buildGroupedLabels(tokens: ILocationToken[] | undefined): ILocationLabelsGroups {
        if (!tokens?.length) {
            return this._labelsGrouper.getGroupedLabels(tokens);
        }

        const uiTokens = this.toUiTokens(tokens);
        const grouped = this._labelsGrouper.getGroupedLabels(uiTokens);

        return {
            isSelectAll: grouped.isSelectAll,
            groups: grouped.groups.map((group) => ({
                parentLabel: this._humanizeParentLabel(group.parentLabel),
                tokens: group.tokens.map((token) => this._toHumanReadableToken(token as ILocationUiToken)),
            })),
        };
    }

    /** Returns the full options tree for the location picker. */
    public getOptionsTree(): ICheckboxesTreeItem[] {
        return this._treeConverter.getOptionsTree();
    }

    /** Normalizes a parsed compare value payload into persistence format. */
    public normalizeCompareValue(parsed: IParsedLocationCompareValue | ILocationToken): ILocationCompareValueList {
        if (this._isVersionTwoCompareValue(parsed)) {
            const list = parsed.list
                ? this.toPersistence(this._normalizeRawTokens(parsed.list))
                : undefined;

            return {_ver: 2, list};
        }

        return {
            _ver: 2,
            list: this.toPersistence([this._createTokenFromRaw(parsed as ILocationToken)]),
        };
    }

    /** Compares token lists by persistence shape (order-independent). */
    public areTokenListsEqual(
        left: ILocationToken[] | undefined,
        right: ILocationToken[] | undefined
    ): boolean {
        return this._semanticGroupConverter.areTokenListsEqual(left, right);
    }

    private _isVersionTwoCompareValue(
        parsed: IParsedLocationCompareValue | ILocationToken
    ): parsed is IParsedLocationCompareValue & { _ver: 2 } {
        return typeof parsed === 'object' && parsed !== null && '_ver' in parsed && parsed._ver === 2;
    }

    private _normalizeRawTokens(tokens: unknown[]): ILocationToken[] {
        return tokens
            .filter((token): token is ILocationToken => {
                if (!token || typeof token !== 'object') {
                    return false;
                }

                const candidate = token as ILocationToken;
                return Boolean(candidate.group || candidate.country);
            })
            .map((token) => this._createTokenFromRaw(token));
    }

    private _createTokenFromRaw(raw: ILocationToken): ILocationToken {
        if (raw.group && !raw.country) {
            return {group: raw.group};
        }

        const token: ILocationToken = {
            group: raw.group ?? null,
            country: raw.country,
        };

        if (raw.province) {
            token.province = raw.province;
        }

        if (raw.city) {
            token.city = raw.city;
        }

        return token;
    }

    private _toHumanReadableToken(token: ILocationUiToken): ILocationToken {
        if (token.group && !token.country) {
            return {group: this._semanticGroupConverter.getGroupName(token.group)};
        }

        return {
            group: token.group
                ? this._semanticGroupConverter.getGroupName(token.group)
                : null,
            country: this._treeConverter.getCountryName(token.country!),
            province: token.province
                ? this._treeConverter.getProvinceName(token.country!, token.province)
                : undefined,
            city: token.city
                ? this._treeConverter.getCityName(token.country!, token.city)
                : undefined,
        };
    }

    private _humanizeParentLabel(parentLabel: string): string {
        if (this._semanticGroupConverter.getSemanticGroups().some((group) => group.label === parentLabel)) {
            return parentLabel;
        }

        return this._treeConverter.getCountryName(parentLabel) || parentLabel;
    }
}

export default LocationSelectionConverter;
